import type {
  AddRiskToEnterpriseRiskInput,
  AddRiskToEnterpriseRiskMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  AddRiskToEnterpriseRiskDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * Cache-invalidation parity with the Apollo `evictField` set
 * (`risk` / `risk_score` / `enterprise_risk` / `enterprise_risk_score`): the
 * enterprise-risk register/detail/tier scores change, and the linked risks'
 * enterprise-risk attribution changes.
 */
export const useAddRiskToEnterpriseRisk = createMutationHook({
  name: 'addRiskToEnterpriseRisk',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.enterpriseRisk.link.mutationOptions({ onSuccess }),
  mapInput: (objects: AddRiskToEnterpriseRiskInput[]) => ({ objects }),
  invalidate: (trpc) => [
    trpc.frontend.enterpriseRisk.register.queryKey(),
    trpc.frontend.enterpriseRisk.getById.queryKey(),
    trpc.frontend.enterpriseRisk.getByTier.queryKey(),
    trpc.frontend.risk.riskById.queryKey(),
    trpc.frontend.risk.register.queryKey(),
  ],
  // Maps the tRPC `{ affectedRows }` response to the GraphQL mutation shape.
  mapResponse: (result: {
    affectedRows: number;
  }): AddRiskToEnterpriseRiskMutation => ({
    addRiskToEnterpriseRisk: {
      __typename: 'GenericMutationOutput',
      affected_rows: result.affectedRows,
    },
  }),
  graphqlDocument: AddRiskToEnterpriseRiskDocument,
  graphqlVariables: (objects: AddRiskToEnterpriseRiskInput[]) => ({ objects }),
  evictFields: [
    'risk',
    'risk_score',
    'enterprise_risk',
    'enterprise_risk_score',
  ],
  refetchQueries: [namedOperations.Query.getRiskById],
  // The risk-detail read is tRPC-only, so its tanstack cache must refresh even
  // when the write ran through the Apollo path.
  invalidateOnGraphql: (trpc) => [trpc.frontend.risk.riskById.queryKey()],
});
